import { Is } from '../types';

// Telefon numarası normalizasyonu - Tüm formatları +905XXXXXXXXX'e çevirir
export const normalizePhoneNumber = (phone: string | null | undefined): string => {
  if (!phone || phone.trim() === '') return '';

  // Tüm boşlukları, parantezleri, tireleri temizle
  let cleaned = phone
    .replace(/\s/g, '')
    .replace(/\(/g, '')
    .replace(/\)/g, '')
    .replace(/-/g, '');

  // +90 varsa kaldır
  if (cleaned.startsWith('+90')) {
    cleaned = cleaned.substring(3);
  }

  // 90 ile başlıyorsa kaldır
  if (cleaned.startsWith('90') && cleaned.length === 12) {
    cleaned = cleaned.substring(2);
  }

  // 0 ile başlıyorsa kaldır
  if (cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1);
  }

  // Sadece 10 haneli numarayı al (5XXXXXXXXX)
  if (cleaned.length === 10 && cleaned.startsWith('5')) {
    return `+90${cleaned}`;
  }

  // Geçersiz format — Google kullanıcılarının telefonu olmayabilir
  return '';
};

// Tarih formatlama - date-fns olmadan
export const formatDate = (date: Date | null | undefined): string => {
  if (!date || isNaN(date.getTime())) {
    return 'Geçersiz tarih';
  }

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Bugün';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Dün';
  } else {
    return date.toLocaleDateString('tr-TR');
  }
};

export const formatDateTime = (date: Date | null | undefined): string => {
  if (!date || isNaN(date.getTime())) {
    return 'Geçersiz tarih';
  }

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  } else if (date.toDateString() === yesterday.toDateString()) {
    return `Dün ${date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
  } else {
    return date.toLocaleDateString('tr-TR') + ' ' + date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  }
};

export const formatTimeAgo = (date: Date | null | undefined): string => {
  if (!date || isNaN(date.getTime())) {
    return 'Geçersiz tarih';
  }

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return `${diffInSeconds} saniye önce`;
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} dakika önce`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} saat önce`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} gün önce`;
  } else if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `${months} ay önce`;
  } else {
    const years = Math.floor(diffInSeconds / 31536000);
    return `${years} yıl önce`;
  }
};

// Telefon numarası formatlama
export const formatPhoneNumber = (phone: string): string => {
  // Sadece rakamları al
  let cleaned = phone.replace(/\D/g, '');

  // Eğer 11 haneli ve 0 ile başlıyorsa, 0'ı kaldır
  if (cleaned.length === 11 && cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1);
  }

  // Eğer 10 haneli ve 5 ile başlıyorsa (Türkiye cep telefonu)
  if (cleaned.length === 10 && cleaned.startsWith('5')) {
    return `+90${cleaned}`;
  }

  // Eğer zaten +90 ile başlıyorsa, olduğu gibi bırak
  if (phone.startsWith('+90')) {
    return phone;
  }

  // Eğer 11 haneli ve 90 ile başlıyorsa, + ekle
  if (cleaned.length === 11 && cleaned.startsWith('90')) {
    return `+${cleaned}`;
  }

  // Diğer durumlar için +90 ekle
  if (cleaned.length === 10) {
    return `+90${cleaned}`;
  }

  return phone;
};

// IBAN formatlama (2 rakam boşluk, 4 rakam boşluk, 4 rakam boşluk, 4 rakam boşluk, 4 rakam boşluk, 4 rakam boşluk, 2 rakam boşluk)
export const formatIBAN = (iban: string): string => {
  // Sadece harf ve rakamları al
  const cleaned = iban.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

  // TR ile başlayan IBAN için özel format
  if (cleaned.startsWith('TR')) {
    // TR + 2 rakam + 4 rakam + 4 rakam + 4 rakam + 4 rakam + 4 rakam + 2 rakam
    if (cleaned.length === 26) {
      return `${cleaned.substring(0, 2)} ${cleaned.substring(2, 4)} ${cleaned.substring(4, 8)} ${cleaned.substring(8, 12)} ${cleaned.substring(12, 16)} ${cleaned.substring(16, 20)} ${cleaned.substring(20, 24)} ${cleaned.substring(24, 26)}`;
    }
  }

  // Diğer IBAN formatları için genel format
  const chunks = [];
  for (let i = 0; i < cleaned.length; i += 4) {
    chunks.push(cleaned.substring(i, i + 4));
  }
  return chunks.join(' ');
};

// Para formatlama
export const formatCurrency = (amount: number, kdvEklendi: boolean = false): string => {
  const formatted = new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);

  return kdvEklendi ? `${formatted} (+KDV)` : formatted;
};

// Büyük harfe çevirme
export const toUpperCase = (text: string | null | undefined): string => {
  return (text || '').toUpperCase();
};

// WhatsApp mesaj oluşturma
export const createWhatsAppMessage = (is: Is): string => {
  const { nereden, nereye, sofor } = is;

  if (!sofor) {
    return `${(nereden || '').toUpperCase()} ➡️ ${(nereye || '').toUpperCase()}📍 YÜKLEMESİ`;
  }

  const fiyat = is.kimOduyor === 'karsi'
    ? formatCurrency(is.fiyat || 0, is.kdvEklendi)
    : formatCurrency(is.bizimFiyatimiz || 0, is.bizimFiyatKdv || false);

  const message = `${(nereden || '').toUpperCase()} ➡ ${(nereye || '').toUpperCase()}📍 YÜKLEMESİ
ARAÇ BİLGİLERİ

AD SOYAD : ${sofor.adSoyad || 'BELİRTİLMEMİŞ'}
PLAKA : ${sofor.plaka || 'BELİRTİLMEMİŞ'}
DORSE : ${sofor.dorse || 'BELİRTİLMEMİŞ'}
TC : ${sofor.tc || 'BELİRTİLMEMİŞ'}
TELEFON 1 : ${formatPhoneNumber(sofor.telefon) || 'BELİRTİLMEMİŞ'}
TELEFON 2 : ${sofor.telefon2 ? formatPhoneNumber(sofor.telefon2) : 'BELİRTİLMEMİŞ'}
ARAÇ TİPİ : ${sofor.aracTipi || 'BELİRTİLMEMİŞ'}
YÜK MİKTARI : ${is.yuk || 'BELİRTİLMEMİŞ'}

NAKLİYE BEDELİ : ${fiyat}
ARAÇ YÜKLEME İÇİN YÖNLENDİRİLMİŞTİR.`;

  return message;
};

// WhatsApp link oluşturma
export const createWhatsAppLink = (phone: string, message: string): string => {
  const formattedPhone = formatPhoneNumber(phone);
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${formattedPhone}?text=${encodedMessage}`;
};

// SMS link oluşturma
export const createSMSLink = (phone: string, message: string): string => {
  const formattedPhone = formatPhoneNumber(phone);
  const encodedMessage = encodeURIComponent(message);
  return `sms:${formattedPhone}?body=${encodedMessage}`;
};

// Telefon arama linki oluşturma
export const createCallLink = (phone: string): string => {
  const formattedPhone = formatPhoneNumber(phone);
  return `tel:${formattedPhone}`;
};

// Panoya kopyalama
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    // Import edilen logger kullanmak yerine direkt console.error kullanıyoruz
    // çünkü bu dosya logger'ın import edemeyeceği kadar temel bir dosya
    if (process.env.NODE_ENV === 'development') {
      console.error('Panoya kopyalama hatası:', error);
    }
    return false;
  }
};

// Dosya indirme (JSON/CSV)
export const downloadJSON = (data: any, filename: string): void => {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

export const downloadCSV = (data: any[], filename: string): void => {
  if (data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row =>
      headers.map(header => {
        const value = row[header];
        if (value instanceof Date) {
          return value.toLocaleDateString('tr-TR') + ' ' + value.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
        }
        return typeof value === 'string' ? `"${value}"` : value;
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

// Arama ve filtreleme
export const filterJobs = (
  jobs: Is[],
  searchTerm: string,
  filters: {
    firma?: string;
    nereden?: string;
    nereye?: string;
    soforAdi?: string;
    plaka?: string;
    tc?: string;
    telefon?: string;
    dorse?: string;
    yukTipi?: string;
    startDate?: Date;
    endDate?: Date;
  }
): Is[] => {
  return jobs.filter(job => {
    // Arama terimi kontrolü
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const searchableFields = [
        job.firma,
        job.nereden,
        job.nereye,
        job.yuk,
        job.sofor?.adSoyad,
        job.sofor?.plaka,
        job.sofor?.tc,
        job.sofor?.telefon,
        job.sofor?.dorse
      ].filter(Boolean);

      const hasMatch = searchableFields.some(field =>
        field?.toLowerCase().includes(searchLower)
      );

      if (!hasMatch) return false;
    }

    // Filtreler
    if (filters.firma && job.firma !== filters.firma) return false;
    if (filters.nereden && job.nereden !== filters.nereden) return false;
    if (filters.nereye && job.nereye !== filters.nereye) return false;
    if (filters.soforAdi && job.sofor?.adSoyad !== filters.soforAdi) return false;
    if (filters.plaka && job.sofor?.plaka !== filters.plaka) return false;
    if (filters.tc && job.sofor?.tc !== filters.tc) return false;
    if (filters.telefon && job.sofor?.telefon !== filters.telefon) return false;
    if (filters.dorse && job.sofor?.dorse !== filters.dorse) return false;
    if (filters.yukTipi && job.yuk !== filters.yukTipi) return false;

    // Tarih filtreleri
    if (filters.startDate && job.eklenmeTarihi < filters.startDate) return false;
    if (filters.endDate && job.eklenmeTarihi > filters.endDate) return false;

    return true;
  });
};

// Kazanç hesaplama
export const calculateProfit = (is: Is): number => {
  if (is.kimOduyor === 'karsi') {
    // Karşı ödeme: Sadece komisyon bizim kazancımız
    return (is.komisyon || 0);
  } else {
    // Biz ödeme: (Müşteri fiyatı - Bize kesilen fiyat) + Komisyon
    return (is.musteriFiyati || 0) - (is.bizimFiyatimiz || 0) + (is.komisyon || 0);
  }
};

// Benzersiz değerler listesi
export const getUniqueValues = (array: any[], key: string): string[] => {
  const values = array.map(item => item[key]).filter(Boolean);
  return [...new Set(values)].sort();
};

// Renk kodları
export const colors = {
  primary: '#1976d2',
  secondary: '#dc004e',
  success: '#4caf50',
  warning: '#ff9800',
  error: '#f44336',
  info: '#2196f3',
  background: '#ffffff',
  surface: '#f5f5f5',
  text: '#212121',
  textSecondary: '#757575'
};

// Bildirim gönderme
export const sendNotification = async (title: string, body: string, icon?: string): Promise<boolean> => {
  try {
    // Service Worker kontrol et
    if ('serviceWorker' in navigator && 'Notification' in window) {
      // Bildirim izni kontrol et
      if (Notification.permission === 'granted') {
        const notification = new Notification(title, {
          body,
          icon: icon || '/assets/images/logo-192.png',
          badge: '/assets/images/logo-192.png',
          vibrate: [100, 50, 100],
          requireInteraction: false,
          silent: false
        });

        // 5 saniye sonra otomatik kapat
        setTimeout(() => {
          notification.close();
        }, 5000);

        return true;
      } else if (Notification.permission !== 'denied') {
        // İzin iste
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          const notification = new Notification(title, {
            body,
            icon: icon || '/assets/images/logo-192.png',
            badge: '/assets/images/logo-192.png',
            vibrate: [100, 50, 100],
            requireInteraction: false,
            silent: false
          });

          // 5 saniye sonra otomatik kapat
          setTimeout(() => {
            notification.close();
          }, 5000);

          return true;
        }
      }
    }
    return false;
  } catch (error) {
    // Import edilen logger kullanmak yerine direkt console.error kullanıyoruz
    // çünkü bu dosya logger'ın import edemeyeceği kadar temel bir dosya
    if (process.env.NODE_ENV === 'development') {
      console.error('Bildirim gönderme hatası:', error);
    }
    return false;
  }
};

// Bildirim izni iste
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  try {
    if ('Notification' in window) {
      return await Notification.requestPermission();
    }
    return 'denied';
  } catch (error) {
    // Import edilen logger kullanmak yerine direkt console.error kullanıyoruz
    // çünkü bu dosya logger'ın import edemeyeceği kadar temel bir dosya
    if (process.env.NODE_ENV === 'development') {
      console.error('Bildirim izni hatası:', error);
    }
    return 'denied';
  }
}; 